import { useMutation, useQueryClient } from "@tanstack/react-query"
import { TruckIcon } from "lucide-react"
import type * as React from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import {
  EWAY_BILL_NUMBER_PATTERN,
  ewayBillRequirement,
  type SupplyType,
} from "@steelbill/types"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { DateField } from "@/components/DateField"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
/*
 * `useAuth().can` RATHER THAN `useCan`, matching `InvoiceDetailScreen`, which
 * already destructures `can` the same way. Both read the identical map in
 * `packages/types` — §2.1's one resolver — so this is a spelling choice and not
 * a different check. It is spelled this way because every test that renders the
 * bill screen stubs this module with a `useAuth` and nothing else; reaching for
 * the second export would have made a new card on this screen break six suites
 * that have nothing to do with e-way bills.
 */
import { useAuth } from "@/features/auth/auth-context"
import { api } from "@/lib/api"
import { ApiError } from "@/lib/api-error"
import { useFormatDay } from "@/lib/dates"
import { endpoints, type DocumentEnvelope, type InvoiceDetail } from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { queryKeys } from "@/lib/query-keys"

/**
 * The e-way bill number, on the bill (Rule 138 CGST, spec §17.7).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * FOUR THINGS WERE SETTLED WITH THE OWNER AND HIS CA IN WRITING, AND EVERY ONE
 * OF THEM SHAPES WHAT IS ON THIS CARD.
 *
 * 1. THE GOODS MOVE ON THE TAX INVOICE. The CA was asked specifically about
 *    works-contract material sent to site — this business's main line, and the
 *    case where a delivery challan looks like the obvious basis — and answered
 *    "Tax invoice". So the number belongs HERE. A challan carries the same three
 *    fields as a secondary case, for movements with no invoice behind them.
 *
 * 2. THIS APP CANNOT GENERATE ONE AND NEVER WILL. Direct e-way bill API access
 *    needs turnover above ₹10 crore; below that, a paid GSP. This business is
 *    far below either. The CA logs into the government portal — not the owner —
 *    and the number is typed in here afterwards.
 *
 * 3. IT IS RECORDED AFTER THE FACT, NOT BEFORE DISPATCH. The portal issues its
 *    own e-way bill PDF carrying NIC's QR, and THAT is what travels with the
 *    driver. No lorry waits for an accountant, and nothing on this screen may be
 *    designed as though one does — which is why there is no "generate", no
 *    blocking state on the invoice, and no step between saving a bill and
 *    printing it.
 *
 * 4. NO QR IS DRAWN ON THIS SCREEN — BUT THE INVOICE PDF DRAWS ONE. The owner
 *    decided on 2026-08-17 (§10.1 amendment) that the e-way bill QR is printed
 *    on the tax invoice: it is plain data — EBN, supplier GSTIN, generation
 *    date, with no signature over it — and TallyPrime builds it locally too, so
 *    it claims nothing about who raised the bill. It is a scan-instead-of-typing
 *    convenience for an officer holding PAPER, which is why this card still
 *    draws nothing: the code belongs on the sheet. The payload lives in
 *    `ewayBillQrPayload` (api services/pdf/theme.ts) and both invoice templates
 *    carry it (§18.3). (The E-INVOICE IRN QR is the signed one that genuinely
 *    cannot be reproduced. Different artifact.)
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * THE THRESHOLD IS COMPUTED BY THE SAME FUNCTION THE SERVER USES.
 * `ewayBillRequirement` lives in `@steelbill/types` and is imported by both
 * surfaces (§2.1) — this screen does not re-derive "is ₹1,13,280 over the
 * limit?" from figures of its own. It is fed the SERVER'S lines and the SERVER'S
 * grand total (§6), so the sentence on this card and the notification the server
 * raised from the same rule cannot disagree about the same bill.
 */
export function EwayBillCard({ invoice }: { invoice: InvoiceDetail }): React.JSX.Element | null {
  const { t } = useTranslation()
  const formatDay = useFormatDay()
  const canRecord = useAuth().can("eway.record")

  const recorded = invoice.ewbNo !== null && invoice.ewbNo !== ""

  /*
   * A CANCELLED bill is withdrawn, so whatever was going to move is not. The
   * server refuses it with a 409 — this hides the control rather than authorising
   * one, exactly as `isEditable` does elsewhere on this screen.
   *
   * A DRAFT IS ELIGIBLE, corrected 2026-08-17. It used to be excluded alongside
   * CANCELLED, on the reading that a draft "has been issued to nobody". The only
   * draft invoice this system can produce is §7.1's conversion without an S.O.
   * number: the number is already spent out of the gap-free series, the material
   * goes to site, and the paperwork waits on the customer's reference — so goods
   * absolutely do move under it. The server agrees and always has: `POST
   * /invoices/:id/ewb` refuses only a CANCELLED bill, so hiding the card on a
   * DRAFT was the client refusing what the API accepts, on the one screen where
   * the ₹1.2 lakh warning most needed to appear.
   */
  const eligible = invoice.status !== "CANCELLED"

  const requirement = ewayBillRequirement({
    /*
     * The server decided this from the two state codes and sent it (§6); the
     * client never re-derives it. Narrowed rather than cast so an unrecognised
     * value falls to the LOWER, national threshold — `ewayBillThresholdPaise`
     * denies by default in the same direction, and over-warning is the safe way
     * to be wrong about whether a lorry needs paperwork.
     */
    supplyType: invoice.supplyType === "INTRA_STATE" ? "INTRA_STATE" : ("INTER_STATE" as SupplyType),
    /*
     * INCLUDING TAX. The explanation to Rule 138(1) defines "consignment value"
     * as the value declared in the invoice INCLUDING central, state and
     * integrated tax. Handing it `subtotalPaise` would let a ₹96,000 + 18% bill
     * — ₹1,13,280 on the road — move unflagged.
     */
    grandTotalPaise: invoice.totals.grandTotalPaise,
    lines: invoice.lines.map((line) => ({
      // Only GOODS count: the obligation attaches to the movement of goods, so a
      // labour-only works-contract bill needs no e-way bill at any value.
      isService: line.isService,
      taxablePaise: line.taxablePaise,
      taxPaise: line.cgstPaise + line.sgstPaise + line.igstPaise,
    })),
  })

  const missing = requirement.required && !recorded && eligible

  /*
   * WHEN THIS CARD IS ABSENT ENTIRELY, which is most bills. Nearly every job
   * this business writes is below the threshold, and an "E-way bill" card on a
   * ₹8,000 invoice is furniture that trains the eye to skip the spot where the
   * real warning will one day appear.
   */
  if (!recorded && !missing && !(canRecord && eligible)) return null

  return (
    <Card data-testid="invoice-eway-bill">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <TruckIcon className="size-4" aria-hidden="true" />
          {t("eway.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/*
          * §17.7's whole reason for existing, said on the DOCUMENT rather than
          * only in a notification. The notification is the nudge — it finds
          * someone who is not looking at this bill; the invoice is the RECORD,
          * and it has to answer "does this one need an e-way bill?" months later
          * to somebody who never saw the alert.
          *
          * IT NAMES BOTH FIGURES. "This needs an e-way bill" invites "are you
          * sure?" from someone looking at a ₹96,000 bill who knows Maharashtra's
          * intra-state limit is ₹1,00,000 — and who is not thinking about the
          * ₹17,280 of GST that puts the consignment at ₹1,13,280. Printing what
          * was compared against what settles that here instead of in a phone
          * call.
          */}
        {missing ? (
          <Alert variant="destructive" data-testid="eway-required">
            <TruckIcon />
            <AlertTitle>{t("eway.requiredTitle")}</AlertTitle>
            <AlertDescription>
              {t("eway.requiredHelp", {
                amount: formatRupees(requirement.consignmentPaise),
                threshold: formatRupees(requirement.thresholdPaise),
              })}
            </AlertDescription>
          </Alert>
        ) : null}

        {recorded ? (
          <RecordedBlock invoice={invoice} formatDay={formatDay} canRecord={canRecord} />
        ) : null}

        {/*
          * THE FORM IS GATED ON `eway.record`, AND ON NOTHING ELSE ABOUT WHO THE
          * PERSON IS (§2.1 — never `user.role`). Recording a fact the government
          * has already issued is not privileged authorship, so the capability is
          * held by the CA, the owner and the son alike; it is deliberately NOT
          * `invoice.create`, because the CA has no business raising invoices.
          *
          * Hidden, never disabled: a greyed-out box on a compliance field invites
          * hunting for the way to un-grey it, and the honest answer for someone
          * without the capability is that this is not their job.
          */}
        {!recorded && canRecord && eligible ? (
          <RecordForm invoice={invoice} startOpen={missing} />
        ) : null}
      </CardContent>
    </Card>
  )
}

/** The number as recorded — read-only, for everybody who can see the bill. */
function RecordedBlock({
  invoice,
  formatDay,
  canRecord,
}: {
  invoice: InvoiceDetail
  formatDay: (iso: string | null | undefined) => string
  canRecord: boolean
}): React.JSX.Element {
  const { t } = useTranslation()
  const clear = useClearEwayBill(invoice.id)
  const [confirming, setConfirming] = useState(false)

  return (
    <div className="flex flex-col gap-3">
      <dl className="grid gap-3 sm:grid-cols-3">
        <div>
          <dt className="text-muted-foreground text-xs">{t("eway.number")}</dt>
          {/*
            * `tabular-nums` and no grouping. It is a 12-digit identifier the
            * reader will compare, digit by digit, against a printed portal sheet
            * — not a quantity. Grouping it would invent a structure the number
            * does not have and make that comparison harder.
            */}
          <dd className="font-medium tabular-nums" data-testid="eway-number">
            {invoice.ewbNo}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs">{t("eway.date")}</dt>
          <dd className="font-medium">{formatDay(invoice.ewbDate)}</dd>
        </div>
        {/*
          * VALIDITY IS SHOWN WHEN THE SHEET STATED ONE, AND NOT INVENTED WHEN IT
          * DID NOT. Rule 138(10) derives validity from distance, and this app
          * neither knows the distance nor computes it — the portal prints the
          * date and the CA copies it across, or leaves it blank. Nothing here
          * warns when it lapses, because there is no scheduler in this system to
          * notice; the field is a record, not an alarm.
          */}
        {invoice.ewbValidUntil === null ? null : (
          <div>
            <dt className="text-muted-foreground text-xs">{t("eway.validUntil")}</dt>
            <dd className="font-medium">{formatDay(invoice.ewbValidUntil)}</dd>
          </div>
        )}
      </dl>

      <p className="text-muted-foreground text-sm">{t("eway.recordedHelp")}</p>

      {/*
        * CLEARING IS THE PORTAL'S CANCEL-AND-REGENERATE PATH, not an undo.
        * An e-way bill cancelled on the portal within 24 hours is replaced by a
        * new one with a new number, and the bill has to be able to carry it. The
        * old number is not lost: it survives in the audit row's before-image,
        * which is why no `ewb_cancelled_at` column exists.
        *
        * Two taps, because it removes a compliance record — and it is at the
        * bottom, small and outline, so it never reads as the thing to do next.
        */}
      {canRecord ? (
        <div className="flex flex-wrap items-center gap-2">
          {confirming ? (
            <>
              <span className="text-sm">{t("eway.clearConfirm")}</span>
              <Button
                variant="destructive"
                size="sm"
                disabled={clear.isPending}
                onClick={() => {
                  clear.mutate(undefined, {
                    onSuccess: () => {
                      setConfirming(false)
                      toast.success(t("eway.cleared"))
                    },
                  })
                }}
              >
                {t("eway.clearYes")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setConfirming(false)
                }}
              >
                {t("btn.cancel")}
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setConfirming(true)
              }}
            >
              {t("eway.clear")}
            </Button>
          )}
        </div>
      ) : null}

      {clear.isError ? (
        <Alert variant="destructive">
          <AlertDescription>
            {clear.error instanceof ApiError ? clear.error.message : t("err.somethingWentWrong")}
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  )
}

/** Typing the number off the portal's sheet. */
function RecordForm({
  invoice,
  startOpen,
}: {
  invoice: InvoiceDetail
  /** Open on a bill that needs one; a disclosure otherwise. */
  startOpen: boolean
}): React.JSX.Element {
  const { t } = useTranslation()
  const [open, setOpen] = useState(startOpen)
  const [ewbNo, setEwbNo] = useState("")
  const [ewbDate, setEwbDate] = useState("")
  const [ewbValidUntil, setEwbValidUntil] = useState("")
  const record = useRecordEwayBill(invoice.id)

  /*
   * VOLUNTARY BELOW-THRESHOLD E-WAY BILLS ARE LEGAL and do happen — a customer's
   * gate may demand one, and works-contract movement is murky enough that the CA
   * sometimes raises one anyway. So the form is reachable on ANY eligible bill,
   * not only on one over the limit. It is collapsed there, to one line of text,
   * so the ordinary ₹8,000 job does not grow a compliance form.
   */
  if (!open) {
    return (
      <div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setOpen(true)
          }}
        >
          {t("eway.recordCta")}
        </Button>
      </div>
    )
  }

  /*
   * THE SAME REGEX THE SERVER VALIDATES WITH, imported rather than retyped
   * (`EwayBillNumberSchema` wraps this exact pattern). SHAPE ONLY: there is no
   * checksum in an EBN and no API to verify one against at this turnover, so a
   * well-formed number is as far as honest validation goes. The LENGTH is the
   * check that earns its keep — a truncated paste is the failure a copy actually
   * produces, and an 11-digit number stored looking valid is worse than a
   * refusal.
   */
  const trimmed = ewbNo.trim()
  const shapeOk = EWAY_BILL_NUMBER_PATTERN.test(trimmed)
  const complete = shapeOk && ewbDate !== ""

  return (
    <div className="flex flex-col gap-3">
      <p className="text-muted-foreground text-sm">{t("eway.recordHelp")}</p>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="eway-number">{t("eway.number")}</Label>
          <Input
            id="eway-number"
            value={ewbNo}
            /*
             * `inputMode="numeric"` gives a phone the digit keypad — this is
             * twelve digits copied off a sheet, and the qwerty keyboard is the
             * wrong tool. NOT `type="number"`: that would offer a spinner on a
             * value nobody increments, and browsers apply numeric coercion that
             * can drop a leading zero.
             */
            inputMode="numeric"
            autoComplete="off"
            maxLength={12}
            placeholder={t("eway.numberPlaceholder")}
            aria-invalid={trimmed !== "" && !shapeOk}
            onChange={(event) => {
              setEwbNo(event.target.value)
            }}
          />
          {trimmed !== "" && !shapeOk ? (
            <p className="text-destructive text-xs">{t("eway.numberShape")}</p>
          ) : null}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="eway-date">{t("eway.date")}</Label>
          <DateField id="eway-date" value={ewbDate} onChange={setEwbDate} clearable />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="eway-valid-until">{t("eway.validUntilOptional")}</Label>
          <DateField
            id="eway-valid-until"
            value={ewbValidUntil}
            onChange={setEwbValidUntil}
            clearable
          />
        </div>
      </div>

      {/*
        * A 409 IS INFORMATION, NOT A TRANSIENT FAILURE — the bill is cancelled,
        * or it already carries a DIFFERENT number. The server's sentence already
        * names which and says what to do about it, so it is shown verbatim rather
        * than replaced with a second sentence composed here.
        */}
      {record.isError ? (
        <Alert variant="destructive">
          <AlertDescription>
            {record.error instanceof ApiError ? record.error.message : t("err.somethingWentWrong")}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          disabled={!complete || record.isPending}
          onClick={() => {
            record.mutate(
              {
                ewbNo: trimmed,
                ewbDate,
                // Absent, not empty: the body is `.strict()` and the column is
                // nullable — "no validity stated on the sheet" is the ordinary
                // case, not a value to be sent as "".
                ewbValidUntil: ewbValidUntil === "" ? undefined : ewbValidUntil,
              },
              {
                onSuccess: () => {
                  toast.success(t("eway.saved"))
                },
              },
            )
          }}
        >
          {record.isPending ? t("msg.saving") : t("btn.save")}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            setOpen(false)
          }}
        >
          {t("btn.cancel")}
        </Button>
      </div>
    </div>
  )
}

interface RecordEwayBillInput {
  ewbNo: string
  ewbDate: string
  ewbValidUntil?: string
}

/**
 * `POST /invoices/:id/ewb`.
 *
 * INVALIDATES THE NOTIFICATIONS TOO, and that is not tidiness. The server raises
 * `invoice.eway_bill.recorded` inside the same transaction as the write, so
 * somebody's bell changed as a direct result of this button — and the person who
 * pressed it may be the one holding the phone that shows it. (The actor is
 * excluded server-side, so it will usually be the OTHER phone; invalidating here
 * costs one request and removes the case where it is not.)
 */
function useRecordEwayBill(id: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: RecordEwayBillInput) => {
      const response = await api.post<DocumentEnvelope<InvoiceDetail>>(
        endpoints.invoiceEwb(id),
        input,
      )
      return response.data
    },
    onSuccess: () => {
      /*
       * THE BILL *AND* THE REGISTER. `useInvoice` caches for five minutes on the
       * explicit grounds that an issued invoice is immutable — true of its
       * lines and totals, and these three columns are the one lawful exception,
       * so without this the screen would go on showing the bill with no number
       * on it immediately after saving one.
       */
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all })
    },
  })
}

/** `DELETE /invoices/:id/ewb` — the portal's cancel-and-regenerate path. */
function useClearEwayBill(id: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await api.delete<DocumentEnvelope<InvoiceDetail>>(
        endpoints.invoiceEwb(id),
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all })
    },
  })
}
